import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Switch,
  Text,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  Input,
  Heading,
  Divider,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiInfo, FiRefreshCw } from 'react-icons/fi';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

// Cast icons as React.ElementType to fix TypeScript issues
const FiRefreshCwIcon = FiRefreshCw as React.ElementType;

export interface DaySchedule {
  isOpen: boolean;
  openTime: string; // "09:00"
  closeTime: string; // "17:00"
}

export interface BusinessHours {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

export interface AppSettings {
  id: string;
  paymentStrategy?: string;
  upfrontPercentage?: number;
  businessHours?: BusinessHours;
  bookingConstraints?: any;
  serviceAreaSettings?: any;
  bookingPoolSettings?: any;
  holidays?: any[];
  technicianSchedules?: any;
  createdAt: any;
  updatedAt: any;
  updatedBy: string;
  policyVersion?: number;
  policyTemplate?: string;
}

const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  monday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
  tuesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
  wednesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
  thursday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
  friday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
  saturday: { isOpen: true, openTime: '09:00', closeTime: '15:00' },
  sunday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
};

const DAY_LABELS: Record<keyof BusinessHours, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const BusinessHoursSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessHours, setBusinessHours] = useState<BusinessHours>(DEFAULT_BUSINESS_HOURS);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        const settings = settingsSnap.data() as AppSettings;
        if (settings.businessHours) {
          setBusinessHours(settings.businessHours);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load business hours settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDay = (day: keyof BusinessHours) => {
    setBusinessHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        isOpen: !prev[day].isOpen,
      },
    }));
  };

  const handleTimeChange = (day: keyof BusinessHours, field: 'openTime' | 'closeTime', value: string) => {
    setBusinessHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!currentUser) {
      toast({
        title: 'Error',
        description: 'You must be logged in to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate business hours
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
    for (const day of days) {
      const daySchedule = businessHours[day];
      if (daySchedule.isOpen) {
        if (!daySchedule.openTime || !daySchedule.closeTime) {
          toast({
            title: 'Validation Error',
            description: `Please set both open and close times for ${DAY_LABELS[day]}`,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(daySchedule.openTime) || !timeRegex.test(daySchedule.closeTime)) {
          toast({
            title: 'Validation Error',
            description: `Invalid time format for ${DAY_LABELS[day]}. Please use HH:MM format.`,
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
      }
    }

    setSaving(true);
    try {
      const settingsRef = doc(db, 'appSettings', 'default');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        await updateDoc(settingsRef, {
          businessHours,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      } else {
        await setDoc(settingsRef, {
          id: 'default',
          paymentStrategy: 'collect_full_at_booking',
          businessHours,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          updatedBy: currentUser.uid,
        });
      }

      toast({
        title: 'Success',
        description: 'Business hours updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save business hours settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setBusinessHours(DEFAULT_BUSINESS_HOURS);
    toast({
      title: 'Reset',
      description: 'Business hours reset to defaults',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  if (loading) {
    return <Text>Loading...</Text>;
  }

  return (
    <Box>
      <Box mb={6}>
        <HStack justify="space-between" mb={4}>
          <Heading 
            size={{ base: 'md', md: 'lg' }}
            pl={{ base: '60px', lg: 0 }}
            pr={{ base: 4, lg: 0 }}
          >
            Business Hours
          </Heading>
          <Button
            leftIcon={<FiRefreshCwIcon />}
            size="sm"
            onClick={handleReset}
          >
            Reset to Defaults
          </Button>
        </HStack>
        
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm">
              Customers can only book appointments during your configured business hours.
              Changes apply to all new bookings immediately.
            </Text>
          </Box>
        </Alert>
      </Box>

      <Card>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {(Object.keys(businessHours) as Array<keyof BusinessHours>).map(day => (
              <Box key={day} p={4} borderWidth={1} borderRadius="lg">
                <HStack justify="space-between" mb={3}>
                  <HStack>
                    <FormControl display="flex" alignItems="center">
                      <Switch
                        isChecked={businessHours[day].isOpen}
                        onChange={() => handleToggleDay(day)}
                        colorScheme="green"
                      />
                      <FormLabel mb={0} ml={3} fontWeight="medium">
                        {DAY_LABELS[day]}
                      </FormLabel>
                      {!businessHours[day].isOpen && (
                        <Text fontSize="sm" color="gray.500" ml={2}>
                          (Closed)
                        </Text>
                      )}
                    </FormControl>
                  </HStack>
                </HStack>

                {businessHours[day].isOpen && (
                  <HStack spacing={4}>
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Open Time</FormLabel>
                      <Input
                        type="time"
                        value={businessHours[day].openTime}
                        onChange={(e) => handleTimeChange(day, 'openTime', e.target.value)}
                      />
                    </FormControl>
                    
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Close Time</FormLabel>
                      <Input
                        type="time"
                        value={businessHours[day].closeTime}
                        onChange={(e) => handleTimeChange(day, 'closeTime', e.target.value)}
                      />
                    </FormControl>
                  </HStack>
                )}
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>

      <HStack spacing={3} mt={6}>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isLoading={saving}
          loadingText="Saving"
        >
          Save Business Hours
        </Button>
      </HStack>
    </Box>
  );
};

export default BusinessHoursSettings;

